import { getUser } from '@go-client/lib/api/user';
import { getStorage, setStorage, unsetStorage } from '@go-client/lib/storage';
import { User } from '@go-shared/types/user';
import React, { useContext, useEffect, useState } from 'react';
import { BubbleOverlay } from '@go-client/components/BubbleUI';

interface RefreshUserInfoContextOptions {
  skipUser?: boolean;
}

export type UserPermissionOverrides = Partial<
  Pick<
    User,
    | 'superAdmin'
    | 'shipAdmin'
    | 'groupAdmin'
    | 'groupUser'
    | 'reserveAdmin'
    | 'reserveUser'
    | 'host'
    | 'reservationManager'
    | 'propertyAdmin'
    | 'libraryAdmin'
    | 'feedEditor'
    | 'canvasEditor'
    | 'brandAdmin'
    | 'viewOnly'
    | 'userAdmin'
  >
>;

export interface UserInfoBundle {
  user: User;
  realUser: User; // user without permission overrides (to disable impersonation)
  isImpersonating: boolean;
  permissionOverrides: UserPermissionOverrides;
  setPermissionOverrides: (overrides: UserPermissionOverrides | undefined) => void;
  refreshContext: (options?: RefreshUserInfoContextOptions) => Promise<void>;
}

const UserInfoContext = React.createContext<UserInfoBundle | undefined>(undefined);

export function useUserInfo() {
  const context = useContext(UserInfoContext);

  if (context === undefined) {
    throw new Error('useUserInfo must be used within a UserInfoProvider');
  }

  return context;
}

export function WithUserInfo(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className: propClasses, children, ...rest } = props;

  const [realUser, setRealUser] = useState<User | undefined>(undefined);
  const [permissionOverrides, setPermissionOverridesState] = useState<UserPermissionOverrides>({});

  async function fetchUserInfo(prevRealUser?: User, options?: RefreshUserInfoContextOptions) {
    const user = options && options.skipUser && prevRealUser ? prevRealUser : await getUser();
    if (user.superAdmin) {
      const stored = getStorage().permissionOverrides;
      if (stored) setPermissionOverridesState(stored as UserPermissionOverrides);
    }
    setRealUser(user);
  }

  useEffect(() => {
    fetchUserInfo().catch(console.error);
  }, []);

  function setPermissionOverrides(overrides: UserPermissionOverrides | undefined) {
    if (!realUser?.superAdmin) return;
    const next = overrides ?? {};
    setPermissionOverridesState(next);
    if (Object.keys(next).length > 0) {
      setStorage('permissionOverrides', next as Record<string, boolean>);
    } else {
      unsetStorage('permissionOverrides');
    }
  }

  const classes = ['h-full'];
  if (propClasses) classes.push(propClasses);

  const isImpersonating = Object.keys(permissionOverrides).length > 0;
  const effectiveUser = realUser ? { ...realUser, ...permissionOverrides } : undefined;

  const userInfoBundle: UserInfoBundle | undefined =
    effectiveUser && realUser
      ? {
          user: effectiveUser,
          realUser,
          isImpersonating,
          permissionOverrides,
          setPermissionOverrides,
          refreshContext: (opts?: RefreshUserInfoContextOptions) => fetchUserInfo(realUser, opts),
        }
      : undefined;

  return (
    <BubbleOverlay loading={!userInfoBundle} {...rest} className={classes.join(' ')}>
      {userInfoBundle && (
        <UserInfoProvider userInfoBundle={userInfoBundle}>{props.children}</UserInfoProvider>
      )}
    </BubbleOverlay>
  );
}

interface UserInfoProviderPropsBase {
  userInfoBundle: UserInfoBundle;
}

type UserInfoProviderProps = UserInfoProviderPropsBase & { children?: React.ReactNode };

export default function UserInfoProvider(props: UserInfoProviderProps) {
  const { userInfoBundle, children } = props;

  return <UserInfoContext.Provider value={userInfoBundle}>{children}</UserInfoContext.Provider>;
}
